<template>
  <BaseModal :modalTitle="$t('W: Opret prøveområde')">
    <!-- Step 1: Name + report template -->
    <UiBaseFormVee
      v-if="currentStep === 1"
      submitOnCtrlEnter
      :errorText="errorText"
      :onSubmit="onStep1Next">
      <UiInputTextFieldVee
        name="name"
        :label="$t('term.name')"
        :placeholder="$t('form.placeholder.name')"
        :rules="['required']"
        isRequired
        :unit="currentLocale.toUpperCase()"
        v-model="form.name" />

      <UiInputSelectVee
        name="labReportTemplateID"
        :label="$t('W: Rapportskabelon')"
        :options="reportTemplateOptions"
        :rules="['required']"
        isRequired
        v-model="form.labReportTemplateID" />

      <template #buttons>
        <UiBaseButton
          :text="$t('W: Næste')"
          type="submit" />
      </template>
    </UiBaseFormVee>

    <!-- Step 2: Select analyses -->
    <UiBaseFormVee
      v-else
      submitOnCtrlEnter
      :errorText="errorText"
      :onSubmit="onSubmit">
      <div>
        <p class="mb-2 block text-sm font-medium text-text-primary">
          {{ $t('W: Valgte analyser') }}
        </p>

        <div
          v-if="form.analysisIDs.length"
          class="mb-4">
          <div
            v-for="analysisID in form.analysisIDs"
            :key="analysisID"
            class="mb-2 flex items-center gap-2 rounded border bg-white p-1">
            <span class="flex-1 text-sm">
              {{ getAnalysisName(analysisID) }}
            </span>
            <button
              type="button"
              class="cursor-pointer p-2 text-text-secondary hover:text-text-primary"
              @click="removeAnalysis(analysisID)">
              <IconLucideX class="h-5 w-5" />
            </button>
          </div>
        </div>

        <UiComboboxMultiVee
          name="analysisIDs"
          :options="analysisOptions"
          :placeholder="$t('term.search')"
          isAddOnly
          :modelValue="form.analysisIDs"
          @update:model-value="onAnalysisIDsUpdate" />
      </div>

      <template #buttons>
        <UiBaseButton
          :text="$t('W: Tilbage')"
          theme="neutral"
          :isDisabled="isLoading"
          @button-click="currentStep = 1" />
        <UiBaseButton
          :text="$t('action.create')"
          :isLoading="isLoading"
          type="submit" />
      </template>
    </UiBaseFormVee>
  </BaseModal>
</template>

<script lang="ts">
import { useToast } from '@/globals/typescript/utils/toast'
import { useSampleAreaStore } from '@/stores/sampleAreaStore'
import { useAnalysisStore } from '@/stores/analysisStore'
import { useReportTemplateStore } from '@/stores/reportTemplateStore'
import { useAppStore } from '@/stores/appStore'
import { mapActions, mapState } from 'pinia'
import { useModalStore } from '@/stores/modalStore'
import { useUnsavedChangesGuard } from '@/composables/useUnsavedChangesGuard'

type TCreateSampleAreaForm = {
  name: string
  labReportTemplateID: string
  analysisIDs: string[]
}

function createForm(): TCreateSampleAreaForm {
  return {
    name: '',
    labReportTemplateID: '',
    analysisIDs: [],
  }
}

export default {
  name: 'CreateSampleAreaModal',

  emits: ['close'],

  data() {
    return {
      unsavedGuard: useUnsavedChangesGuard(),
      form: createForm(),
      currentStep: 1,
      isLoading: false,
      errorText: '',
    }
  },

  computed: {
    ...mapState(useReportTemplateStore, ['allReportTemplates']),
    ...mapState(useAnalysisStore, ['allAnalyses']),
    ...mapState(useAppStore, ['currentLocale']),

    reportTemplateOptions(): { text: string; value: string }[] {
      return [...this.allReportTemplates]
        .sort((a, b) =>
          a
            .getTitle(this.currentLocale)
            .localeCompare(b.getTitle(this.currentLocale))
        )
        .map((template) => ({
          text: template.getTitle(this.currentLocale),
          value: template.id,
        }))
    },

    analysisOptions(): { value: string; text: string }[] {
      return [...this.allAnalyses]
        .filter((x) => x.activeState === 'active')
        .sort((a, b) =>
          a
            .getName(this.currentLocale)
            .localeCompare(b.getName(this.currentLocale))
        )
        .map((analysis) => ({
          value: analysis.id,
          text: analysis.getName(this.currentLocale),
        }))
    },
  },

  methods: {
    ...mapActions(useSampleAreaStore, ['createSampleArea']),
    ...mapActions(useAnalysisStore, ['updateAnalysis']),

    onAnalysisIDsUpdate(newAnalysisIDs: string[]) {
      const addedID = newAnalysisIDs.find(
        (id) => !this.form.analysisIDs.includes(id)
      )
      if (addedID) this.addAnalysis(addedID)
    },

    addAnalysis(analysisID: string) {
      this.form.analysisIDs.push(analysisID)
      this.form.analysisIDs.sort((a, b) => {
        const nameA =
          this.allAnalyses
            .find((x) => x.id === a)
            ?.getName(this.currentLocale) ?? ''
        const nameB =
          this.allAnalyses
            .find((x) => x.id === b)
            ?.getName(this.currentLocale) ?? ''
        return nameA.localeCompare(nameB)
      })
    },

    removeAnalysis(analysisID: string) {
      const idx = this.form.analysisIDs.indexOf(analysisID)
      if (idx !== -1) this.form.analysisIDs.splice(idx, 1)
    },

    getAnalysisName(analysisID: string): string {
      const analysis = this.allAnalyses.find((x) => x.id === analysisID)
      return analysis ? analysis.getName(this.currentLocale) : analysisID
    },

    async onStep1Next() {
      this.errorText = ''
      this.currentStep = 2
    },

    async onSubmit() {
      this.isLoading = true
      this.errorText = ''

      try {
        const newSampleAreaID = await this.createSampleArea({
          name: this.form.name,
          labReportTemplateID: this.form.labReportTemplateID,
        })

        await Promise.all(
          this.form.analysisIDs.map(async (analysisID) => {
            const analysis = this.allAnalyses.find((x) => x.id === analysisID)
            if (!analysis) return
            await this.updateAnalysis(analysisID, {
              sampleAreaIDs: [...analysis.sampleAreaIDs, newSampleAreaID],
            })
          })
        )

        this.unsavedGuard.takeSnapshot()
        useToast().success(this.$t('W: Prøveområde oprettet'))
        this.$emit('close')
      } catch {
        this.errorText = this.$t('error.common.standard')
        useToast().error(this.$t('W: Kunne ikke oprette prøveområde'))
      } finally {
        this.isLoading = false
      }
    },
  },

  created() {
    // The modal is always the top entry at the moment it is created, so this
    // is its own ModalEntry id — it lets ModalHost tell "the modal I am about
    // to close is dirty" apart from "something underneath it is dirty".
    this.unsavedGuard.register({
      snapshot: () => ({ ...this.form }),
      modalId: useModalStore().top?.id,
    })
  },

  beforeUnmount() {
    this.unsavedGuard.unregister()
  },
}
</script>
